import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { useDebounce } from "../hooks/useDebounce";
import { fetchOrders } from "../store/slices/ordersSlice";
import EnhancedTable from "../components/common/EnhancedTable";
import OrderViewModal from "../components/dialogs/OrderViewModal";
import { toast } from "react-toastify";
import { orderAPI } from "../services/api";
import ExportImportButtons from "../components/common/ExportImportButtons";
// import OrderStatusChangeModal from "../components/dialogs/OrderStatusChangeModal";
import { useNavigate } from "react-router-dom";
import StatusChip from "../utils/StatusChip";
import PdfPreviewer from "../components/common/PdfPreviewer";

interface OrderDetail {
  id: string;
  quantity: number;
  amount: number;
  variation: string | null;
  productId: string;
  created: string;
  modified: string;
}

interface OrderUser {
  id: string;
  firstname: string | null;
  lastname: string | null;
  username: string;
  email: string;
  cellnumber: string;
  userstatus: string;
  created: string;
  modified: string;
}

interface OrderCity {
  id: string;
  name: string;
  status: number;
  created: string;
  modified: string;
}

interface Order {
  id: string;
  orderno: string;
  expecteddelivery: string | null;
  orderstatus: number;
  totalamount: number;
  inshoppingcart: number;
  deliverydate: string | null;
  createdby: string;
  modifiedby: string;
  date: string;
  customername: string | null;
  totalitems: number;
  description: string | null;
  paymentDetail: string | null;
  address: string | null;
  baseprice: number | null;
  tax: number | null;
  additionalcharge: number | null;
  additionalchargedetails: string | null;
  shippingprice: number | null;
  discount: number | null;
  paymentstatus: number;
  ordermedia: any | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  userId: string;
  categoryId: string | null;
  cityId: string;
  user: OrderUser;
  category: any | null;
  city: OrderCity;
  orderdetails: OrderDetail[];
  ordernotes: any[];
  productmedia: any[];
  reason?: string;
  agentNumber?: string;
}

const orderStatusValueMap: Record<string, number> = {
  pending: 1,
  inprocess: 2,
  completed: 5,
  cancelled: 7,
  onhold: 8,
  cancelled_points_reversed: 9,
  failed: 10,
  refunded: 11,
  reversed: 12,
  chargeback: 13,
  expired: 14,
  processed: 15,
  voided: 16,
  outofstock: 17,
  client_revert_pending: 18,
  partially_complete: 19,
  redeemed: 20,
  partial_cancel_points_back: 21,
  partially_refunded: 22,
};

const OrdersList: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const dispatch = useAppDispatch();
  const { orders, loading, pagination } = useAppSelector(
    (state: any) => state.orders
  );

  // Column filter state
  const [columnFilters, setColumnFilters] = useState({
    oc_cart_order_id: "",
    totalamount: "",
    date: "",
    totalitems: "",
    city: "",
    agentNumber: "",
    orderstatus: "all",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  // const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  // const [orderToChangeStatus, setOrderToChangeStatus] = useState<Order | null>(
  //   null
  // );

  // const [isloading, setisLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  // Column reordering state
  const [columns, setColumns] = useState([
    {
      id: "oc_cart_order_id",
      label: "Order Number",
      minWidth: 150,
      draggable: true,
      format: (value: string, row: any) => {
        const display = value && String(value).trim() !== "" ? `#${value}` : row?.orderno ? `#${row.orderno}` : "-";
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: "text.secondary" }}
          >
            {display}
          </Typography>
        );
      },
    },
    {
      id :"agentNumber",
      label: "Agent Number",
      minWidth: 150,
      draggable: true,
      format: (value: string, row: any) => {
        const display = value && String(value).trim() !== "" ? value : row?.agentNumber ? row.agentNumber : "-";
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: "text.secondary" }}
          >
            {display}
          </Typography>
        );
      },

    },
    {
      id: "totalamount",
      label: "Redeem Points",
      minWidth: 150,
      align: "center" as const,
      sortable: true,
      draggable: true,
      format: (value: number) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {value?.toLocaleString() || "0"}
        </Typography>
      ),
    },
    {
      id: "date",
      label: "Date",
      minWidth: 150,
      sortable: true,
      draggable: true,
      format: (value: string) => {
        if (!value)
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        const d = new Date(value);
        const formatted = d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {formatted}
          </Typography>
        );
      },
    },
    {
      id: "totalitems",
      label: "Total Items",
      minWidth: 120,
      align: "center" as const,
      draggable: true,
      format: (value: number) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {value || 0}
        </Typography>
      ),
    },
    {
      id: "orderstatus",
      label: "Status",
      minWidth: 120,
      align: "center" as const,
      draggable: true,
      format: (value: number) => <StatusChip status={value} isOrder={true} />,
    },
  ]);

  // Ref to track if column order has been initialized
  const isInitialized = React.useRef(false);

  // Debounce column filters
  const debouncedOrderNo = useDebounce(columnFilters.oc_cart_order_id, 500);
  const debouncedTotalAmount = useDebounce(columnFilters.totalamount, 500);
  const debouncedDate = useDebounce(columnFilters.date, 500);
  const debouncedTotalItems = useDebounce(columnFilters.totalitems, 500);
  const debouncedCity = useDebounce(columnFilters.city, 500);
  const debouncedAgentNumber = useDebounce(columnFilters.agentNumber, 500);
  // const debouncedOrderStatus = useDebounce(columnFilters.orderstatus, 500);

  // Handle column reordering
  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    // Optionally save to localStorage for persistence
    localStorage.setItem(
      "ordersTableColumnOrder",
      JSON.stringify(newColumns.map((col: any) => col.id))
    );
  };

  const handleInvoiceClick = async (orderId: string) => {
    try {
      const response = await orderAPI.getorderInvoiceDownloadUrl(orderId);
      // If response is binary (Blob), create an object URL
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfOpen(true);
    } catch (err) {
      toast.error("Failed to load invoice PDF");
    }
  };

  // Load saved column order on component mount
  React.useEffect(() => {
    // Only run once on mount
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedColumnOrder = localStorage.getItem("ordersTableColumnOrder");
    if (savedColumnOrder) {
      try {
        const savedIds = JSON.parse(savedColumnOrder);
        const reorderedColumns = savedIds
          .map((id: string) => columns.find((col) => col.id === id))
          .filter(Boolean);
        const existingIds = reorderedColumns.map((col: any) => col.id);
        const newColumns = columns.filter(
          (col: any) => !existingIds.includes(col.id)
        );

        setColumns([...reorderedColumns, ...newColumns]);
      } catch (error) {
        console.error("Error loading saved column order:", error);
      }
    }
  }, [columns]); // Now safe to include 'columns' in dependency array

  // Load orders with current filters and pagination
  const loadOrders = useCallback(() => {
    const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
    };

    if (debouncedOrderNo.trim()) {
      params.search = debouncedOrderNo.trim();
    }
    if (debouncedTotalAmount.trim()) {
      params["where[totalamount]"] = debouncedTotalAmount.trim();
    }
    if (debouncedDate) {
      params["where[created]"] = debouncedDate;
    }
    if (debouncedTotalItems.trim()) {
      params["where[totalitems]"] = debouncedTotalItems.trim();
    }
    if (debouncedCity.trim()) {
      params["where[city]"] = debouncedCity.trim();
    }
    if (debouncedAgentNumber.trim()) {
      params["where[agentNumber]"] = debouncedAgentNumber.trim();
    }
    if (columnFilters.orderstatus && columnFilters.orderstatus !== "all") {
      const statusCode =
        orderStatusValueMap[columnFilters.orderstatus.toLowerCase()];
      if (statusCode) {
        params["where[orderstatus]"] = statusCode;
      }
    }
    if (sortField) {
      params.sort = `${sortField} ${sortDirection}`;
    }
    dispatch(fetchOrders(params));
  }, [
    dispatch,
    currentPage,
    itemsPerPage,
    debouncedOrderNo,
    debouncedTotalAmount,
    debouncedDate,
    debouncedTotalItems,
    debouncedCity,
    debouncedAgentNumber,
    columnFilters.orderstatus,
    sortField,
    sortDirection,
  ]);

  // Load orders on mount and when filters/pagination change
  useEffect(() => {
    if (currentPage > 0 && itemsPerPage > 0) {
      loadOrders();
    }
  }, [
    loadOrders,
    currentPage,
    itemsPerPage,
    debouncedOrderNo,
    debouncedTotalAmount,
    debouncedDate,
    debouncedTotalItems,
    debouncedCity,
    debouncedAgentNumber,
    columnFilters.orderstatus,
    sortField,
    sortDirection,
  ]);

  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // const handleOrderStatusSubmit = async (status: number, reason?: string) => {
  //   if (!orderToChangeStatus) return;

  //   const statusCodeToApiLabel: Record<number, string> = {
  //     1: "PENDING",
  //     2: "CONFIRMED",
  //     3: "INPROGRESS",
  //     4: "DELIVERED",
  //     5: "CANCELLED",
  //     6: "REJECTED",
  //   };

  //   const orderId = orderToChangeStatus.id;
  //   const payload: any = {
  //     orderstatus: statusCodeToApiLabel[status],
  //     id: orderId,
  //   };

  //   if (reason) {
  //     payload.reason = reason;
  //   }
  //   setisLoading(true);
  //   try {
  //     await orderAPI.updateOrderStatus(orderId, payload);
  //     loadOrders();
  //     toast.success(`Order Status Changed Successfully`);
  //     setStatusChangeDialogOpen(false);
  //   } catch (error: any) {
  //     console.error("Error updating order status:", error);
  //     toast.error(
  //       error.response?.data?.detail || "Failed to update order status"
  //     );
  //     setStatusChangeDialogOpen(false);
  //   } finally {
  //     setisLoading(false);
  //   }
  // };

  const navigate = useNavigate();

  const handleViewOrder = (order: Order) => {
    navigate(`/order/${order.id}`);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedOrder(null);
  };

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: "#fff",
          boxShadow: "0 4px 18px -4px rgba(0,0,0,0.12)",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
            Orders
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review and export order data.
          </Typography>
        </Box>
        <ExportImportButtons
          onExport={async () => {
            // If there are no orders loaded, show an informative toast and skip API call
            if (!Array.isArray(orders) || orders.length === 0) {
              toast.info("There is no order data to export");
              return;
            }

            try {
              const response = await orderAPI.exportOrders();
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", "orders_export.xlsx");
              document.body.appendChild(link);
              link.click();
              link.remove();
              toast.success("Exported order data successfully");
            } catch (error: any) {
              if (
                error?.response?.status === 404 &&
                error?.response?.data?.detail === "Sorry, No orders available!"
              ) {
                toast.info("No orders are available");
              } else {
                toast.error("Failed to export orders");
              }
            }
          }}
          onImport={() => toast.info("Import functionality coming soon")}
          disabledImport={true}
        />
      </Box>

      <EnhancedTable
        columns={columns}
        data={Array.isArray(orders) ? orders : []}
        loading={loading}
        onView={handleViewOrder}
        showViewCondition={(row: Order) => true}
        showStatusChangeCondition={(row: Order) => false}
        showActions={true}
        emptyMessage="No orders found"
        useExternalPagination={true}
        pagination={pagination}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        useExternalSorting={true}
        onSort={handleSort}
        currentSortField={sortField}
        currentSortDirection={sortDirection}
        enableColumnReordering={true}
        onColumnReorder={handleColumnReorder}
        columnFilters={columnFilters}
        onColumnFilterChange={handleColumnFilterChange}
        statusFilterOptions={{
          orderstatus : [
            { value: "all", label: "All" },
            { value: "pending", label: "Pending" },
            { value: "inprocess", label: "In Process" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
            { value: "onhold", label: "On Hold" },
            {
              value: "cancelled_points_reversed",
              label: "Cancelled - Points Reversed",
            },
            { value: "failed", label: "Failed" },
            { value: "refunded", label: "Refunded" },
            { value: "reversed", label: "Reversed" },
            { value: "chargeback", label: "Chargeback" },
            { value: "expired", label: "Expired" },
            { value: "processed", label: "Processed" },
            { value: "voided", label: "Voided" },
            { value: "outofstock", label: "Out of Stock" },
            { value: "client_revert_pending", label: "Client Revert Pending" },
            { value: "partially_complete", label: "Partially Complete" },
            { value: "redeemed", label: "Redeemed" },
            {
              value: "partial_cancel_points_back",
              label: "Partial Cancel - Points Back",
            },
            { value: "partially_refunded", label: "Partially Refunded" },
          ],
        }}
        onSelectionChange={() => {}}
        showSelection={false}
        enableStickyFilters
        entityName="order"
        onInvoiceClick={(order: Order) => handleInvoiceClick(order.id)}
      />

      <OrderViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        order={selectedOrder}
      />

      {pdfOpen && pdfUrl && (
        <PdfPreviewer
          open={pdfOpen}
          file={{ name: "Invoice.pdf", url: pdfUrl }} // <-- pass as object
          onClose={() => setPdfOpen(false)}
        />
      )}

      {/* <OrderStatusChangeModal
        open={statusChangeDialogOpen}
        onClose={() => setStatusChangeDialogOpen(false)}
        onSubmit={handleOrderStatusSubmit}
        currentStatus={orderToChangeStatus?.orderstatus || 1}
        orderId={orderToChangeStatus?.id || ""}
        isLoading={isloading}
        reason={orderToChangeStatus?.reason || ""}
      /> */}
    </Box>
  );
};

export default OrdersList;
